/**
 * SftpConfigModal — config UI for the `sftp_ftp` leg-node type (batch
 * SFTP/FTP file-drop; migration 029).
 *
 * Built in the same self-contained inline-style idiom as
 * CloudDataDestinationConfigModal. The user picks SFTP or FTP, enters the
 * connection + credentials, a Daily/Weekly schedule, and the same
 * Desired / Fields / Filter By / Unique options as the .CSV download. The
 * server delivers one CSV per window (Daily = last 24h, Weekly = last 7 days,
 * cut on the account timezone) to the target directory.
 *
 * Credentials are write-only from the browser's perspective: the server sends
 * back a mask (never the ciphertext). Leaving a credential field blank keeps
 * the stored value; typing a new value replaces it. "Test Connection" writes a
 * tiny probe file to the target dir and deletes it to prove auth + write
 * permission.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  getBrainFields, getSftpRuns, getSftpHealth, testSftpConnection,
  SftpDeliveryRun, SftpHealth,
} from '../../services/api';
import { BrainField } from '../../types';
import { SYSTEM_FIELDS } from './systemFields';
import ClientAssignmentField from '../shared/ClientAssignmentField';

// Mirrors the backend mask sentinel (legNodeSecrets.CLOUD_CRED_MASK).
const CRED_MASK = '__STORED__';

type Protocol = 'sftp' | 'ftp';
interface FieldRow { name: string; required: boolean; }
interface FilterRow { id: string; field: string; operator: string; value: string; }

interface Props {
  legNode: any;                     // LegNode; .config.sftp holds our config
  onSave: (config: any) => void;
  onClose: () => void;
  /** Called after the Client field writes an assignment. */
  onClientAssigned?: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 10, width: 'min(720px, 96vw)',
  maxHeight: '92vh', overflowY: 'auto', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', margin: '12px 0 4px' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const help: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 3 };

const OPERATORS = [
  ['equals', 'Equals'], ['not_equals', 'Not Equals'], ['contains', 'Contains'],
  ['not_contains', 'Not Contains'], ['greater_than', 'Greater Than'], ['less_than', 'Less Than'],
  ['exists', 'Exists'], ['not_exists', 'Not Exists'], ['regex', 'Regex'],
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

let filterSeq = 0;
const newFilterId = () => `f${Date.now()}_${filterSeq++}`;

const SftpConfigModal: React.FC<Props> = ({ legNode, onSave, onClose, onClientAssigned }) => {
  const existing = legNode?.config?.sftp ?? {};
  const dataBodyId: string = legNode?.dataBodyId || legNode?.data_body_id || '';

  const [protocol, setProtocol] = useState<Protocol>(existing.protocol || 'sftp');
  const [host, setHost] = useState<string>(existing.host || '');
  const [port, setPort] = useState<string>(existing.port ? String(existing.port) : '');
  const [username, setUsername] = useState<string>(existing.username || '');
  const [authMethod, setAuthMethod] = useState<'password' | 'key'>(existing.authMethod === 'key' ? 'key' : 'password');
  const [secure, setSecure] = useState<boolean>(!!existing.secure);

  const creds = existing.credentials || {};
  const hasStoredPassword = creds.password === CRED_MASK;
  const hasStoredKey = creds.privateKey === CRED_MASK;
  const hasStoredPassphrase = creds.passphrase === CRED_MASK;
  const [password, setPassword] = useState<string>('');
  const [privateKey, setPrivateKey] = useState<string>('');
  const [passphrase, setPassphrase] = useState<string>('');

  const [remoteDir, setRemoteDir] = useState<string>(existing.remoteDir || '/');
  const [filenameTemplate, setFilenameTemplate] = useState<string>(existing.filenameTemplate || 'leads_{YYYYMMDD}.csv');

  const [frequency, setFrequency] = useState<'daily' | 'weekly'>(existing.frequency === 'weekly' ? 'weekly' : 'daily');
  const [flushHour, setFlushHour] = useState<number>(Number.isInteger(existing.flushHour) ? existing.flushHour : 6);
  const [weekday, setWeekday] = useState<number>(Number.isInteger(existing.weekday) ? existing.weekday : 1);

  const [totalDesired, setTotalDesired] = useState<string>(existing.totalDesired ? String(existing.totalDesired) : '');
  const [fields, setFields] = useState<FieldRow[]>(
    Array.isArray(existing.fields)
      ? existing.fields.map((f: any) => ({ name: f.name || '', required: !!f.required })).filter((f: FieldRow) => f.name)
      : []
  );
  const [filters, setFilters] = useState<FilterRow[]>(
    Array.isArray(existing.filterRules)
      ? existing.filterRules.map((r: any) => ({ id: newFilterId(), field: r.field || '', operator: r.operator || 'equals', value: r.value ?? '' }))
      : []
  );
  const [unique, setUnique] = useState<boolean>(!!existing.unique);
  const [writeMode, setWriteMode] = useState<'overwrite' | 'append_suffix'>(existing.writeMode === 'append_suffix' ? 'append_suffix' : 'overwrite');

  const [brainFields, setBrainFields] = useState<BrainField[]>([]);
  const [runs, setRuns] = useState<SftpDeliveryRun[]>([]);
  const [health, setHealth] = useState<SftpHealth | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!dataBodyId) {return;}
    getBrainFields(dataBodyId).then(setBrainFields).catch(() => { /* leave empty */ });
    if (legNode?.id) {
      getSftpRuns(dataBodyId, legNode.id).then(setRuns).catch(() => { /* best-effort */ });
      getSftpHealth(dataBodyId, legNode.id).then(setHealth).catch(() => { /* best-effort */ });
    }
  }, [dataBodyId, legNode?.id]);

  const availableToAdd = useMemo(
    () => brainFields.filter(b => !fields.some(f => f.name === b.fieldName)),
    [brainFields, fields]
  );

  const addFieldByName = (name: string) =>
    setFields(fs => fs.some(f => f.name === name) ? fs : [...fs, { name, required: false }]);
  const removeFieldByName = (name: string) =>
    setFields(fs => fs.filter(f => f.name !== name));
  const setRequiredByName = (name: string, required: boolean) =>
    setFields(fs => fs.map(f => f.name === name ? { ...f, required } : f));

  const addFilter = () => setFilters(fs => [...fs, { id: newFilterId(), field: '', operator: 'equals', value: '' }]);
  const updateFilter = (id: string, patch: Partial<FilterRow>) =>
    setFilters(fs => fs.map(f => f.id === id ? { ...f, ...patch } : f));
  const removeFilter = (id: string) => setFilters(fs => fs.filter(f => f.id !== id));

  // Assemble the sftp config block from current form state.
  const buildSftp = (): any => {
    const cleanFields = fields.map(f => ({ name: f.name.trim(), required: !!f.required })).filter(f => f.name);
    const cleanFilters = filters
      .map(f => ({ field: f.field.trim(), operator: f.operator, value: f.value }))
      .filter(f => f.field);
    const sftp: any = {
      protocol,
      host: host.trim(),
      port: port.trim() ? Math.max(1, Math.min(65535, Number(port) || 0)) : undefined,
      username: username.trim(),
      remoteDir: remoteDir.trim() || '/',
      filenameTemplate: filenameTemplate.trim() || 'leads_{YYYYMMDD}.csv',
      frequency,
      flushHour,
      fields: cleanFields,
      filterRules: cleanFilters,
      unique,
      writeMode,
      // Blank field => send the mask so the server keeps the stored secret.
      credentials: {
        password: password || (hasStoredPassword ? CRED_MASK : ''),
        privateKey: privateKey || (hasStoredKey ? CRED_MASK : ''),
        passphrase: passphrase || (hasStoredPassphrase ? CRED_MASK : ''),
      },
    };
    if (protocol === 'sftp') {sftp.authMethod = authMethod;}
    if (protocol === 'ftp') {sftp.secure = secure;}
    if (frequency === 'weekly') {sftp.weekday = weekday;}
    if (totalDesired.trim()) {sftp.totalDesired = Math.max(1, Number(totalDesired) || 0);}
    return sftp;
  };

  const validate = (): string | null => {
    if (!host.trim()) {return 'Host is required.';}
    if (!username.trim()) {return 'Username is required.';}
    if (fields.filter(f => f.name.trim()).length === 0) {return 'Add at least one field to include in the file.';}
    return null;
  };

  const handleTest = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(''); setTestResult(''); setTesting(true);
    try {
      const res = await testSftpConnection(dataBodyId, legNode?.id || 'new', buildSftp());
      setTestResult(res.ok
        ? `✓ Connected and wrote a test file (${res.latencyMs} ms).`
        : `✗ ${res.error || 'Connection failed.'}`);
      if (legNode?.id) {getSftpHealth(dataBodyId, legNode.id).then(setHealth).catch(() => { /* ignore */ });}
    } catch (e: any) {
      setTestResult(`✗ ${e?.message || 'Connection failed.'}`);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    const v = validate();
    if (v) { setError(v); return; }
    onSave({ ...(legNode.config || {}), sftp: buildSftp() });
  };

  const healthBadge = () => {
    const s = health?.status || 'unknown';
    const color = s === 'ok' ? '#059669' : s === 'error' ? '#dc2626' : '#9ca3af';
    const text = s === 'ok' ? 'Connection OK' : s === 'error' ? 'Connection error' : 'Not checked yet';
    return (
      <span style={{ fontSize: 12, color, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
        {text}
        {s === 'error' && health?.error ? ` · ${health.error.slice(0, 60)}` : ''}
        {s === 'ok' && health?.lastOkAt ? ` · ${new Date(health.lastOkAt).toLocaleString()}` : ''}
      </span>
    );
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>SFTP &amp; FTP</h2>
            <p style={{ ...help, marginTop: 4 }}>
              Automatically deliver a CSV of this Brain's leads to an SFTP or FTP server on a Daily or Weekly schedule, cut on the account timezone.
            </p>
          </div>
          <div style={{ textAlign: 'right', paddingTop: 4 }}>{healthBadge()}</div>
        </div>

        {/* Client — deliberately the first thing in the modal. Who a
            destination delivers to is the only decision in here that
            cannot be undone once a lead has gone out. Self-contained:
            it fetches and writes the assignment itself rather than
            riding on this modal's own save flow. */}
        <ClientAssignmentField
          dataBodyId={legNode.dataBodyId}
          legNodeId={legNode.id}
          nodeType={legNode.type}
          onAssigned={onClientAssigned}
        />

        {/* ---- Connection ---- */}
        <div style={row2}>
          <div>
            <label style={label}>Protocol</label>
            <select style={input} value={protocol} onChange={e => setProtocol(e.target.value as Protocol)}>
              <option value="sftp">SFTP (SSH)</option>
              <option value="ftp">FTP</option>
            </select>
          </div>
          <div>
            <label style={label}>Port</label>
            <input style={input} value={port} onChange={e => setPort(e.target.value)}
              placeholder={protocol === 'sftp' ? '22 (default)' : '21 (default)'} />
          </div>
        </div>

        <div style={row2}>
          <div>
            <label style={label}>Host</label>
            <input style={input} value={host} onChange={e => setHost(e.target.value)} placeholder="sftp.client.com" />
          </div>
          <div>
            <label style={label}>Username</label>
            <input style={input} value={username} onChange={e => setUsername(e.target.value)} placeholder="username" autoComplete="off" />
          </div>
        </div>

        {protocol === 'sftp' && (
          <>
            <label style={label}>Authentication</label>
            <select style={input} value={authMethod} onChange={e => setAuthMethod(e.target.value as any)}>
              <option value="password">Password</option>
              <option value="key">Private key</option>
            </select>
          </>
        )}

        {protocol === 'ftp' && (
          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={secure} onChange={e => setSecure(e.target.checked)} />
            Use explicit FTPS (TLS)
          </label>
        )}

        {(protocol === 'ftp' || authMethod === 'password') && (
          <>
            <label style={label}>Password</label>
            <input style={input} type="password" autoComplete="new-password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={hasStoredPassword ? '•••• stored — leave blank to keep' : 'password'} />
          </>
        )}

        {protocol === 'sftp' && authMethod === 'key' && (
          <>
            <label style={label}>Private key (OpenSSH)</label>
            <textarea style={{ ...input, minHeight: 90, fontFamily: 'monospace', fontSize: 12 }} value={privateKey}
              onChange={e => setPrivateKey(e.target.value)}
              placeholder={hasStoredKey ? '•••• stored — leave blank to keep' : '-----BEGIN OPENSSH PRIVATE KEY-----'} />
            <label style={label}>Key passphrase (optional)</label>
            <input style={input} type="password" autoComplete="new-password" value={passphrase}
              onChange={e => setPassphrase(e.target.value)}
              placeholder={hasStoredPassphrase ? '•••• stored — leave blank to keep' : 'passphrase (if the key is encrypted)'} />
          </>
        )}
        <div style={help}>Credentials are encrypted at rest and never shown again after saving.</div>

        <div style={row2}>
          <div>
            <label style={label}>Remote directory</label>
            <input style={input} value={remoteDir} onChange={e => setRemoteDir(e.target.value)} placeholder="/uploads/leads" />
          </div>
          <div>
            <label style={label}>Filename template</label>
            <input style={input} value={filenameTemplate} onChange={e => setFilenameTemplate(e.target.value)} />
          </div>
        </div>
        <div style={help}>Filename tokens: {'{YYYYMMDD}'} {'{YYYY-MM-DD}'} {'{YYYY}'} {'{MM}'} {'{DD}'}.</div>

        {/* ---- Test connection ---- */}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" onClick={handleTest} disabled={testing}
            style={{ padding: '8px 14px', border: '1px solid #2563eb', borderRadius: 6, background: '#eff6ff', color: '#2563eb', cursor: testing ? 'default' : 'pointer' }}>
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          {testResult && (
            <span style={{ fontSize: 13, color: testResult.startsWith('✓') ? '#059669' : '#dc2626' }}>{testResult}</span>
          )}
        </div>
        <div style={help}>Test writes a tiny probe file to the target directory and deletes it — proves login and write permission.</div>

        {/* ---- Schedule ---- */}
        <div style={row2}>
          <div>
            <label style={label}>Frequency</label>
            <select style={input} value={frequency} onChange={e => setFrequency(e.target.value as any)}>
              <option value="daily">Daily — most recent 24 hours</option>
              <option value="weekly">Weekly — most recent 7 days</option>
            </select>
          </div>
          <div>
            <label style={label}>Delivery hour (account local, 0–23)</label>
            <input style={input} type="number" min={0} max={23} value={flushHour}
              onChange={e => setFlushHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))} />
          </div>
        </div>
        {frequency === 'weekly' && (
          <>
            <label style={label}>Day of week</label>
            <select style={input} value={weekday} onChange={e => setWeekday(Number(e.target.value))}>
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </>
        )}

        {/* ---- Desired ---- */}
        <label style={label}>Total Desired (max rows per file)</label>
        <input style={input} type="number" min={1} value={totalDesired}
          onChange={e => setTotalDesired(e.target.value)} placeholder="leave blank for no extra cap" />

        {/* ---- Fields ---- */}
        <label style={label}>Fields (Brain fields to include)</label>
        {fields.length === 0 && <div style={help}>No fields selected yet — add them from the dropdown below.</div>}
        {fields.map((f) => (
          <div key={f.name} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ flex: 1, fontSize: 13, padding: '6px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>{f.name}</span>
            <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={f.required} onChange={e => setRequiredByName(f.name, e.target.checked)} /> required
            </label>
            <button type="button" onClick={() => removeFieldByName(f.name)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        {(() => {
          const availableSystem = SYSTEM_FIELDS.filter(s => !fields.some(f => f.name === s.id));
          return (
            <select style={input} value=""
              disabled={availableToAdd.length === 0 && availableSystem.length === 0}
              onChange={e => { if (e.target.value) {addFieldByName(e.target.value);} }}>
              <option value="">
                {availableToAdd.length === 0 && availableSystem.length === 0 ? '— All fields added —' : '+ Add a field…'}
              </option>
              {availableToAdd.map(b => <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>)}
              <optgroup label="System Fields">
                {availableSystem.map(s => <option key={s.id} value={s.id} title={`e.g. ${s.example}`}>{s.label} — {s.example}</option>)}
              </optgroup>
            </select>
          );
        })()}
        <div style={help}>The CSV header is the field names, in this order. "required" drops rows missing that field. Blank rows are never written.</div>

        {/* ---- Filter By ---- */}
        <label style={label}>Filter By</label>
        {filters.length === 0 && (
          <div style={{ ...help, padding: '6px 0' }}>No filters — every lead in the window is included. All rules must pass (AND).</div>
        )}
        {filters.map(rule => (
          <div key={rule.id} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <select style={{ ...input, flex: 1 }} value={rule.field} onChange={e => updateFilter(rule.id, { field: e.target.value })}>
              <option value="">{brainFields.length === 0 ? '— No Brain fields —' : '— Pick a field —'}</option>
              {brainFields.map(b => <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>)}
            </select>
            <select style={{ ...input, width: 150 }} value={rule.operator} onChange={e => updateFilter(rule.id, { operator: e.target.value })}>
              {OPERATORS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
            </select>
            <input style={{ ...input, flex: 1 }} placeholder="Value" value={rule.value} onChange={e => updateFilter(rule.id, { value: e.target.value })} />
            <button type="button" onClick={() => removeFilter(rule.id)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={addFilter} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, marginTop: 4 }}>+ Add Filter</button>

        {/* ---- Unique ---- */}
        <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={unique} onChange={e => setUnique(e.target.checked)} />
          Unique — exclude leads this destination has already delivered
        </label>

        {/* ---- Re-run behavior ---- */}
        <label style={label}>Re-run behavior</label>
        <select style={input} value={writeMode} onChange={e => setWriteMode(e.target.value as any)}>
          <option value="overwrite">Overwrite the window's file (same filename)</option>
          <option value="append_suffix">Write a new suffixed file each run</option>
        </select>

        {/* ---- History ---- */}
        {runs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <label style={label}>Recent deliveries</label>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 160, overflowY: 'auto' }}>
              {runs.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                  <span>{r.partitionDate}</span>
                  <span style={{ color: r.status === 'completed' ? '#059669' : r.status === 'failed' ? '#dc2626' : '#6b7280' }}>
                    {r.status}{r.status === 'completed' ? ` · ${r.rowCount} rows` : ''}{r.error ? ` · ${r.error.slice(0, 60)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={handleSave} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default SftpConfigModal;
